// Modules
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';

// Types
import type { TSelectOption, TSelectOptionTopLeft, TSelectProps, TSelectStatus } from './Select.type';

// Styles
import styles from './styles/styles.module.scss';

// Components
import Icon from '../Icon';
import SelectOption from './SelectOption';

// Constants
import {
	SELECT_CLEAR_SEARCH_ID,
	SELECT_DEFAULT_PROPS, SELECT_OPTION_ROOT_ID, SELECT_OPTION_TIMEOUT,
	SELECT_STATUS,
	SELECT_VARIANT
} from './Select.constant';
import { ICON_NAME } from '../Icon/Icon.constant';

// Helpers
import { generateID, isEmpty } from '../../helpers/common';
import { isBoolean } from '../../helpers/typeChecker';

const getCaptionIcon = (status: TSelectStatus) => {
	switch (status) {
		case SELECT_STATUS.error:
			return ICON_NAME['error-triangle'];
		case SELECT_STATUS.success:
			return ICON_NAME['success-circle'];
		default:
			return ICON_NAME['info-circle'];
	}
};

const Select = (props: TSelectProps) => {
	const {
		block,
		caption,
		disabled,
		hint,
		id,
		label,
		multiple,
		onChange,
		onChangeSearch,
		onClearSearch,
		optionClassName,
		options,
		optionsWrapperClassName,
		placeholder,
		ref,
		searchPlaceholder,
		searchValue,
		searchable,
		size = SELECT_DEFAULT_PROPS.size,
		status = SELECT_DEFAULT_PROPS.status,
		value,
		variant = SELECT_DEFAULT_PROPS.variant,
		...restProps
	} = props;
	const isOutline = variant === SELECT_VARIANT.outline;
	const selectRef = useRef<HTMLDivElement | null>(null);
	const localRef = useRef<HTMLInputElement | null>(null);
	const inputRef = ref || localRef;
	const optionRef = useRef<HTMLDivElement | null>(null);

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [isShow, setIsShow] = useState<boolean>(false);
	const [selectedOption, setSelectedOption] = useState<string>();
	const [optionTopLeft, setOptionTopLeft] = useState<TSelectOptionTopLeft>({
		top: 0,
		left: 0,
		width: 0,
	});

	const selectCN = cn(styles.legion_select, styles[size!], styles[status!], {
		[styles.block]: block,
		[styles.disabled]: disabled,
		[styles[variant!]]: variant,
		[styles.open]: isOpen,
	});
	const optionCN = cn(styles.select_option, optionsWrapperClassName);

	const inputId = `legion_select_${id || generateID()}`;
	const captionIcon = getCaptionIcon(status!);

	const handleChangeSearch = (value: string) => {
		onChangeSearch?.(value);
	}

	const handleClearSearch = () => {
		onChangeSearch?.('');
		onClearSearch?.();
	}

	const handleToggleSelect = (newIsOpen: boolean = !isOpen) => {
		if (!disabled) {
			const validNewIsOpen = !isBoolean(newIsOpen) ? !isOpen : newIsOpen;
			setIsOpen(validNewIsOpen);
		}
	}

	const handleClickInput = () => {
		handleToggleSelect()
	}

	const handleOptionClick = (selected: TSelectOption) => {
		if (multiple && Array.isArray(value)) {
			let selectedValues = isEmpty(value) ? [] : [...value]
			let labelArray = isEmpty(selectedOption) ? [] : selectedOption!.split(', ')

			if (selectedValues.includes(selected.value)) {
				selectedValues = selectedValues.filter(val => val !== selected.value)
				labelArray = labelArray.filter(label => label !== selected.label)
			} else {
				selectedValues = [...selectedValues, selected.value]
				labelArray = [...labelArray, selected.label]
			}

			setSelectedOption(!isEmpty(labelArray!) ? labelArray.join(', ') : '')
			onChange?.(selectedValues);
		} else {
			handleToggleSelect(false);
			setSelectedOption(selected.label)
			onChange?.(selected.value);
		}
	}

	const handleSelectOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleToggleSelect();
		} else if (e.key === 'Tab' && isOpen) {
			setIsOpen(false);
			selectRef?.current!.focus();
		}
	}

	useEffect(() => {
		const optionRoot = document.getElementById(SELECT_OPTION_ROOT_ID) ?? document.createElement('div');
		optionRoot.id = SELECT_OPTION_ROOT_ID;
		document.body.appendChild(optionRoot);

		let initialSelectedOption: string;

		if (Array.isArray(value)) {
			initialSelectedOption = options.filter(option => value.includes(option.value)).map(option => option.label).join(', ')
		} else {
			initialSelectedOption = options.find(option => option.value === value)?.label || ''
		}

		setSelectedOption(initialSelectedOption)

		return () => {
			if (document.getElementById(SELECT_OPTION_ROOT_ID)) {
				document.body.removeChild(optionRoot);
			}
		}
	}, [])

	useEffect(() => {
		const handleBlurSelect = (e: Event) => {
			const target = e.target as HTMLElement;

			if (
				(!!selectRef?.current && !selectRef?.current!.contains(target))
				&& (!!optionRef?.current && !optionRef?.current!.contains(target))
				&& target.id !== SELECT_CLEAR_SEARCH_ID
			) {
				handleToggleSelect(false)
			}
		}

		const disposeOption = setTimeout(() => {
			setIsShow(false);
		}, SELECT_OPTION_TIMEOUT)

		if (isOpen) {
			handleClearSearch();
			setIsShow(true)
			clearTimeout(disposeOption)
			document.addEventListener('click', handleBlurSelect);
		} else {
			document.removeEventListener('click', handleBlurSelect);
		}

		return () => {
			clearTimeout(disposeOption)
			document.removeEventListener('click', handleBlurSelect);
		}
	}, [isOpen]);


	useEffect(() => {
		const selectRect = selectRef?.current!.getBoundingClientRect();
		setOptionTopLeft({
			top: selectRect.top + selectRect.height || 0,
			left: selectRect.left || 0,
			width: selectRect.width || 0
		})
	}, [selectRef.current?.offsetTop,
		selectRef.current?.offsetLeft,
		selectRef.current?.offsetWidth,
		selectRef.current?.offsetHeight])

	return (
		<>
			<div className={selectCN}>
				{label && (
					<label
						htmlFor={inputId}
						className={styles.select_label}
					>
						{label}
					</label>
				)}
				<div
					tabIndex={0}
					ref={selectRef}
					className={styles.input_wrapper}
					onKeyDown={handleSelectOnKeyDown}
					onClick={handleClickInput}
				>
					<input
						ref={inputRef}
						className={styles.select_input}
						id={inputId}
						placeholder={placeholder}
						defaultValue={selectedOption}
						disabled={disabled}
						readOnly
						{...restProps}
					/>
					{isOutline && (
						<svg
							xmlns={'http://www.w3.org/2000/svg'}
						>
							<rect height={'100%'} width={'100%'} fill={'none'}/>
						</svg>
					)}
					<i className={styles.input_icon}/>
				</div>
				{caption && (
					<p className={styles.select_caption}>
						<Icon className={styles.caption_icon} icon={captionIcon}/>
						{caption}
					</p>
				)}
				{hint && (
					<p className={styles.select_hint}>
						{hint}
					</p>
				)}
			</div>
			{isShow && createPortal(
				(
					<SelectOption
						ref={optionRef}
						className={optionCN}
						optionClassName={optionClassName}
						style={{
							...optionTopLeft,
							top: `calc(${optionTopLeft.top}px + var(--select-padding-modal-gap))`
						}}
						isOpen={isOpen}
						activeOption={value}
						options={options}
						size={size}
						searchable={searchable}
						searchPlaceholder={searchPlaceholder}
						searchValue={searchValue}
						onClick={handleOptionClick}
						onChangeSearch={handleChangeSearch}
						onClearSearch={handleClearSearch}
					/>
				),
				document.getElementById(SELECT_OPTION_ROOT_ID) || document.body
			)}
		</>
	);
}

export default Select;
